import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputClass = "w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none transition-colors";
const cardClass = "bg-gray-900 border border-yellow-400/20 rounded-2xl p-6";
const buttonClass = "bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-bold";
const secondaryButtonClass = "bg-gray-800 hover:bg-gray-700 text-yellow-400 px-4 py-2 rounded-lg font-bold border border-yellow-400/30";

const SUB_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'lectures', label: 'Lectures' },
  { id: 'materials', label: 'Course Materials' },
  { id: 'assignments', label: 'Assignments' },
  { id: 'schedule', label: 'Class Schedule' },
  { id: 'attendance', label: 'Attendance' },
  { id: 'discussions', label: 'Discussions' },
  { id: 'resources', label: 'Resources' },
];

const RESOURCE_LIKE_CATEGORIES = new Set(['reference', 'reading', 'resource']);

const EmptyState = ({ title, message }) => (
  <div className={cardClass}>
    <h3 className="text-yellow-400 font-bold mb-1">{title}</h3>
    <p className="text-gray-400 text-sm">{message}</p>
  </div>
);

const resourceActionLabel = (resourceType) => {
  switch (resourceType) {
    case 'video': return 'Watch Video';
    case 'pdf': return 'Download PDF';
    case 'document': return 'Download Document';
    case 'external_link': return 'Open Link';
    case 'assignment': return 'View Assignment';
    default: return 'Open Resource';
  }
};

const resolveProtectedUrl = (url) => {
  if (!url) return '';
  const resolved = /^https?:\/\//i.test(url) ? url : `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  if (!/\/api\/(files|employee)\//i.test(resolved)) return resolved;
  try {
    const token = localStorage.getItem('token');
    if (!token) return resolved;
    const separator = resolved.includes('?') ? '&' : '?';
    return `${resolved}${separator}access_token=${encodeURIComponent(token)}`;
  } catch (error) {
    return resolved;
  }
};

const StudentCoursePage = ({ courseId, onBack }) => {
  const [subTab, setSubTab] = useState('overview');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessMessage, setAccessMessage] = useState('You are not enrolled in this course, or it no longer exists.');
  const [course, setCourse] = useState(null);
  const [progressPercent, setProgressPercent] = useState(0);
  const [courseClasses, setCourseClasses] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  const [lectures, setLectures] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loadedTabs, setLoadedTabs] = useState({});

  const safeCall = useCallback(async (fn) => {
    try {
      setError('');
      await fn();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setAccessDenied(false);
        setLoadedTabs({});
        const res = await axios.get(`${API}/dashboard/student`);
        if (!active) return;
        const data = res.data || {};
        const enrolledCourse = (data.enrolled_courses || []).find((c) => c.id === courseId);
        if (!enrolledCourse) {
          const pending = (data.enrollment_requests || []).find((row) => row.course_id === courseId && ['pending', 'pending_review', 'additional_information_required', 'waitlisted'].includes(String(row.status || '').toLowerCase()));
          if (pending) {
            setAccessMessage('Enrollment is pending review. Course lessons and resources unlock after approval.');
          } else {
            setAccessMessage('Payment required or enrollment not active for this course.');
          }
          setAccessDenied(true);
          return;
        }
        if (enrolledCourse.access_granted === false) {
          setAccessMessage(
            String(enrolledCourse.payment_status || '').toLowerCase() === 'paid'
              ? 'Enrollment is pending activation for this course.'
              : 'Payment required. Complete payment verification or contact admin for access.'
          );
          setAccessDenied(true);
          return;
        }
        setCourse(enrolledCourse);
        const progressRow = (data.learning_progress || []).find((p) => p.course_id === courseId);
        setProgressPercent(progressRow?.progress_percent || 0);
        setCourseClasses((data.upcoming_classes || []).filter((c) => c.course_id === courseId));
        setAttendanceRecords((data.attendance?.history || []).filter((a) => a.course_id === courseId));
      } catch (e) {
        setAccessDenied(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [courseId]);

  const fetchLectures = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/courses/${courseId}/lectures`);
    setLectures(res.data.lectures || []);
  }), [courseId, safeCall]);

  const fetchMaterials = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/courses/${courseId}/materials`);
    setMaterials(res.data.materials || []);
  }), [courseId, safeCall]);

  const fetchAnnouncements = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/courses/${courseId}/announcements`);
    setAnnouncements(res.data.announcements || []);
  }), [courseId, safeCall]);

  const fetchDiscussions = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/courses/${courseId}/discussions`);
    setDiscussions(res.data.discussions || []);
  }), [courseId, safeCall]);

  const fetchAssignments = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/student/assignments`, { params: { course_id: courseId } });
    setAssignments(res.data.assignments || []);
  }), [courseId, safeCall]);

  const fetchMeetings = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/student/meetings/upcoming`);
    const classIds = new Set(courseClasses.map((c) => c.id));
    setMeetings((res.data.meetings || []).filter((m) => classIds.has(m.class_id)));
  }), [courseClasses, safeCall]);

  useEffect(() => {
    if (!course || loadedTabs[subTab]) return;
    const loaders = {
      overview: async () => { await Promise.all([fetchAnnouncements(), fetchLectures()]); },
      announcements: fetchAnnouncements,
      lectures: fetchLectures,
      materials: fetchMaterials,
      resources: fetchMaterials,
      assignments: fetchAssignments,
      schedule: fetchMeetings,
      discussions: fetchDiscussions,
    };
    const loader = loaders[subTab];
    if (loader) {
      loader();
      setLoadedTabs((prev) => ({ ...prev, [subTab]: true }));
    }
  }, [subTab, course]);

  if (loading) {
    return <div className="text-gray-400">Loading course workspace...</div>;
  }

  if (accessDenied || !courseId) {
    return (
      <div className={cardClass}>
        <h3 className="text-yellow-400 font-bold mb-2">Payment Required</h3>
        <p className="text-gray-400 text-sm mb-4">{accessMessage}</p>
        {onBack && <button onClick={onBack} className={buttonClass}>Back to My Courses</button>}
      </div>
    );
  }

  const courseMaterials = materials.filter((m) => !RESOURCE_LIKE_CATEGORIES.has(m.category));
  const resourceMaterials = materials.filter((m) => RESOURCE_LIKE_CATEGORIES.has(m.category));
  const upcomingClass = [...courseClasses].sort((a, b) => (a.date || '').localeCompare(b.date || ''))[0];
  const latestAnnouncement = announcements[0];

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          {onBack && <button onClick={onBack} className="text-yellow-400 hover:text-yellow-300 text-sm font-medium mb-2">&larr; Back to My Courses</button>}
          <h2 className="text-2xl font-bold text-yellow-400">{course.title}</h2>
          <p className="text-gray-400 text-sm mt-1">Teacher: {course.teacher_name || course.instructor || 'K.R.A Instructor Team'} &bull; {course.duration || 'Approximately 4 Months'}</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/40 border border-red-500 text-red-200 rounded-xl p-4 text-sm">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-gray-800 pb-4">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${subTab === t.id ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'overview' && (
        <div className="space-y-6">
          <div className={cardClass}>
            <h3 className="text-yellow-400 font-bold mb-2">Course Description</h3>
            <p className="text-gray-300 text-sm">{course.description || 'Course description will be provided by your instructor.'}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={cardClass}><div className="text-gray-400 text-sm">Teacher</div><div className="text-yellow-400 font-bold">{course.instructor_name || course.teacher_name || course.instructor || 'K.R.A Team'}</div></div>
            <div className={cardClass}><div className="text-gray-400 text-sm">Duration</div><div className="text-yellow-400 font-bold">{course.course_duration || course.duration || 'Approximately 4 Months'}</div></div>
            <div className={cardClass}><div className="text-gray-400 text-sm">Progress</div><div className="text-yellow-400 font-bold">{progressPercent}%</div></div>
            <div className={cardClass}><div className="text-gray-400 text-sm">Upcoming Class</div><div className="text-yellow-400 font-bold">{upcomingClass ? `${upcomingClass.date}` : 'None scheduled'}</div></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={cardClass}>
              <h3 className="text-yellow-400 font-bold mb-2">Learning Objectives</h3>
              <ul className="text-gray-300 text-sm space-y-1">
                {(course.learning_objectives || course.what_you_will_learn || []).slice(0, 8).map((item, idx) => <li key={idx}>• {item}</li>)}
                {(!(course.learning_objectives || course.what_you_will_learn || []).length) && <li>Objectives will be shared by your instructor.</li>}
              </ul>
            </div>
            <div className={cardClass}>
              <h3 className="text-yellow-400 font-bold mb-2">Skills Gained</h3>
              <ul className="text-gray-300 text-sm space-y-1">
                {(course.skills_developed || course.learning_outcomes || []).slice(0, 8).map((item, idx) => <li key={idx}>• {item}</li>)}
                {(!(course.skills_developed || course.learning_outcomes || []).length) && <li>Skills and outcomes will be updated as modules are published.</li>}
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={cardClass}><div className="text-gray-400 text-sm">Enrollment Date</div><div className="text-yellow-400 font-bold">{course.enrollment_date ? new Date(course.enrollment_date).toLocaleDateString() : 'Recently enrolled'}</div></div>
            <div className={cardClass}><div className="text-gray-400 text-sm">Enrollment Status</div><div className="text-yellow-400 font-bold capitalize">{String(course.enrollment_status || 'active').replaceAll('_', ' ')}</div></div>
            <div className={cardClass}><div className="text-gray-400 text-sm">Payment Status</div><div className="text-yellow-400 font-bold capitalize">{String(course.payment_status || 'unknown').replaceAll('_', ' ')}</div></div>
          </div>

          <div className={cardClass}>
            <h3 className="text-yellow-400 font-bold mb-2">Latest Announcement</h3>
            {latestAnnouncement ? (
              <div>
                <h4 className="text-gray-100 font-semibold">{latestAnnouncement.title}</h4>
                <p className="text-gray-400 text-sm mt-1">{latestAnnouncement.content}</p>
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No announcements yet.</p>
            )}
          </div>
        </div>
      )}

      {subTab === 'announcements' && (
        <div className="space-y-4">
          {announcements.length === 0 && <EmptyState title="No announcements yet" message="Announcements posted by your teacher will appear here." />}
          {announcements.map((a) => (
            <div key={a.id} className={cardClass}>
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-yellow-400 font-bold">{a.title}</h4>
                {a.is_pinned && <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded-full font-bold">Pinned</span>}
              </div>
              <p className="text-gray-300 text-sm">{a.content}</p>
              <p className="text-gray-500 text-xs mt-2">{course.teacher_name || course.instructor || 'Teacher'} &bull; {a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</p>
            </div>
          ))}
        </div>
      )}

      {subTab === 'lectures' && (
        <div className="space-y-4">
          {lectures.length === 0 && <EmptyState title="No lectures yet" message="Videos, slides, and documents uploaded by your teacher will appear here." />}
          {Object.entries(
            lectures.reduce((acc, l) => {
              const key = l.module_label || 'General';
              acc[key] = acc[key] || [];
              acc[key].push(l);
              return acc;
            }, {})
          ).map(([moduleLabel, moduleLectures]) => (
            <div key={moduleLabel} className={cardClass}>
              <h4 className="text-yellow-400 font-bold mb-3">{moduleLabel}</h4>
              <div className="space-y-3">
                {moduleLectures.map((l) => (
                  <div key={l.id} className="bg-gray-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <h5 className="text-gray-100 font-semibold">{l.title}</h5>
                      <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full capitalize">{l.resource_type}</span>
                    </div>
                    {l.description && <p className="text-gray-400 text-sm mb-2">{l.description}</p>}
                    <a href={resolveProtectedUrl(l.resource_url)} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm hover:underline">{resourceActionLabel(l.resource_type)}</a>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {subTab === 'materials' && (
        <div className="space-y-4">
          {courseMaterials.length === 0 && <EmptyState title="No course materials yet" message="Worksheets, practice sets, and coding files uploaded by your teacher will appear here." />}
          {courseMaterials.map((m) => (
            <div key={m.id} className={cardClass}>
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-yellow-400 font-bold">{m.title}</h4>
                <span className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full capitalize">{m.category}</span>
              </div>
              {m.description && <p className="text-gray-400 text-sm mb-2">{m.description}</p>}
              <a href={resolveProtectedUrl(m.resource_url)} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm hover:underline">Download</a>
            </div>
          ))}
        </div>
      )}

      {subTab === 'assignments' && (
        <AssignmentsTab courseId={courseId} assignments={assignments} refresh={fetchAssignments} setError={setError} />
      )}

      {subTab === 'schedule' && (
        <div className="space-y-4">
          {courseClasses.length === 0 && <EmptyState title="No classes scheduled" message="Upcoming classes for this course will appear here." />}
          {courseClasses.map((c) => {
            const linkedMeeting = meetings.find((m) => m.class_id === c.id);
            const meetingUrl = c.meeting_link || linkedMeeting?.meeting_url;
            return (
              <div key={c.id} className={cardClass}>
                <h4 className="text-yellow-400 font-bold mb-1">{c.name}</h4>
                <p className="text-gray-400 text-sm">{c.date} &bull; {c.start_time} - {c.end_time}</p>
                <p className="text-gray-400 text-sm">Instructor: {c.teacher_name || course.teacher_name || 'K.R.A Team'}</p>
                {meetingUrl ? (
                  <a href={meetingUrl} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm hover:underline mt-2 inline-block">Join Meeting Link</a>
                ) : (
                  <p className="text-gray-500 text-xs mt-2">Meeting link not available yet.</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {subTab === 'attendance' && (
        <AttendanceSummaryTab records={attendanceRecords} />
      )}

      {subTab === 'discussions' && (
        <DiscussionsTab courseId={courseId} discussions={discussions} refresh={fetchDiscussions} setError={setError} />
      )}

      {subTab === 'resources' && (
        <div className="space-y-4">
          {resourceMaterials.length === 0 && <EmptyState title="No additional resources yet" message="Helpful websites, references, and recommended reading will appear here." />}
          {resourceMaterials.map((m) => (
            <div key={m.id} className={cardClass}>
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-yellow-400 font-bold">{m.title}</h4>
                <span className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full capitalize">{m.category}</span>
              </div>
              {m.description && <p className="text-gray-400 text-sm mb-2">{m.description}</p>}
              <a href={resolveProtectedUrl(m.resource_url)} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm hover:underline">Open Resource</a>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Assignments (view + submit)
// ---------------------------------------------------------------------------
const AssignmentsTab = ({ assignments, refresh, setError }) => {
  const [drafts, setDrafts] = useState({});

  const submit = async (assignmentId) => {
    const draft = drafts[assignmentId] || {};
    if (!draft.submission_url && !draft.submission_text) {
      setError('Add a submission link or text before submitting.');
      return;
    }
    try {
      await axios.post(`${API}/student/assignments/${assignmentId}/submit`, {
        submission_url: draft.submission_url || null,
        submission_text: draft.submission_text || null,
      });
      setDrafts((prev) => ({ ...prev, [assignmentId]: {} }));
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to submit assignment.');
    }
  };

  return (
    <div className="space-y-4">
      {assignments.length === 0 && <EmptyState title="No assignments yet" message="Assignments for this course will appear here." />}
      {assignments.map((a) => {
        const draft = drafts[a.id] || {};
        const submission = a.submission;
        return (
          <div key={a.id} className={cardClass}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h4 className="text-yellow-400 font-bold mb-1">{a.title}</h4>
                <p className="text-gray-300 text-sm mb-1">{a.description}</p>
                <p className="text-gray-400 text-xs">{a.due_text} &bull; Max score: {a.max_score}</p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full font-bold ${a.submitted ? 'bg-green-800 text-green-200' : 'bg-gray-700 text-gray-300'}`}>
                {a.submitted ? 'Submitted' : 'Pending'}
              </span>
            </div>

            {submission && (
              <div className="bg-gray-800 rounded-lg p-3 mt-3">
                <p className="text-gray-300 text-sm">Your submission: {submission.submission_url ? <a href={submission.submission_url} target="_blank" rel="noreferrer" className="text-yellow-400 hover:underline">{submission.submission_url}</a> : submission.submission_text}</p>
                {submission.grade !== null && submission.grade !== undefined ? (
                  <p className="text-yellow-400 text-sm mt-1">Grade: {submission.grade} / {a.max_score}{submission.feedback ? ` — ${submission.feedback}` : ''}</p>
                ) : (
                  <p className="text-gray-500 text-xs mt-1">Not graded yet.</p>
                )}
              </div>
            )}

            <div className="mt-3 space-y-2">
              <input
                className={inputClass}
                placeholder="Submission link (optional)"
                value={draft.submission_url || ''}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [a.id]: { ...prev[a.id], submission_url: e.target.value } }))}
              />
              <textarea
                className={`${inputClass} min-h-[80px]`}
                placeholder="Submission text (optional)"
                value={draft.submission_text || ''}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [a.id]: { ...prev[a.id], submission_text: e.target.value } }))}
              />
              <button onClick={() => submit(a.id)} className={buttonClass}>{submission ? 'Resubmit' : 'Submit Assignment'}</button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Attendance (read-only summary)
// ---------------------------------------------------------------------------
const AttendanceSummaryTab = ({ records }) => {
  const total = records.length;
  const present = records.filter((r) => r.status === 'present').length;
  const absent = records.filter((r) => r.status === 'absent').length;
  const late = records.filter((r) => r.status === 'late').length;
  const excused = records.filter((r) => r.status === 'excused').length;
  const percent = total ? Math.round(((present + late) / total) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className={cardClass}><div className="text-gray-400 text-sm">Attendance %</div><div className="text-yellow-400 text-2xl font-bold">{percent}%</div></div>
        <div className={cardClass}><div className="text-gray-400 text-sm">Present</div><div className="text-yellow-400 text-2xl font-bold">{present}</div></div>
        <div className={cardClass}><div className="text-gray-400 text-sm">Late</div><div className="text-yellow-400 text-2xl font-bold">{late}</div></div>
        <div className={cardClass}><div className="text-gray-400 text-sm">Absent</div><div className="text-yellow-400 text-2xl font-bold">{absent}</div></div>
        <div className={cardClass}><div className="text-gray-400 text-sm">Excused</div><div className="text-yellow-400 text-2xl font-bold">{excused}</div></div>
      </div>
      {records.length === 0 && <EmptyState title="No attendance records yet" message="Your attendance for this course will appear here once classes are recorded." />}
      {records.map((r) => (
        <div key={r.id} className={cardClass}>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-gray-100 font-semibold">{r.class_name || 'Class Session'}</h4>
              <p className="text-gray-400 text-xs">{r.date}</p>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full font-bold capitalize ${
              r.status === 'present' ? 'bg-green-800 text-green-200' :
              r.status === 'late' ? 'bg-yellow-800 text-yellow-100' :
              r.status === 'excused' ? 'bg-blue-900 text-blue-200' : 'bg-red-900 text-red-200'
            }`}>{r.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Discussions (view + ask + reply; teacher moderates)
// ---------------------------------------------------------------------------
const DiscussionsTab = ({ courseId, discussions, refresh, setError }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', body: '' });
  const [openThread, setOpenThread] = useState(null);
  const [replies, setReplies] = useState({});
  const [replyText, setReplyText] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/courses/${courseId}/discussions`, form);
      setShowForm(false);
      setForm({ title: '', body: '' });
      refresh();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to post discussion.');
    }
  };

  const loadReplies = async (discussionId) => {
    if (openThread === discussionId) { setOpenThread(null); return; }
    setOpenThread(discussionId);
    try {
      const res = await axios.get(`${API}/courses/${courseId}/discussions/${discussionId}/replies`);
      setReplies((prev) => ({ ...prev, [discussionId]: res.data.replies || [] }));
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to load replies.');
    }
  };

  const postReply = async (discussionId) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API}/courses/${courseId}/discussions/${discussionId}/replies`, { body: replyText });
      setReplyText('');
      const res = await axios.get(`${API}/courses/${courseId}/discussions/${discussionId}/replies`);
      setReplies((prev) => ({ ...prev, [discussionId]: res.data.replies || [] }));
      refresh();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to post reply.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><button onClick={() => setShowForm(true)} className={buttonClass}>+ Ask a Question</button></div>
      {discussions.length === 0 && <EmptyState title="No discussions yet" message="Ask a course-related question to get started." />}
      {discussions.map((d) => (
        <div key={d.id} className={cardClass}>
          <div className="cursor-pointer" onClick={() => loadReplies(d.id)}>
            <h4 className="text-yellow-400 font-bold mb-1">{d.title}</h4>
            <p className="text-gray-300 text-sm">{d.body}</p>
            <p className="text-gray-400 text-xs mt-1">{d.author_name} &bull; {d.reply_count || 0} replies</p>
          </div>
          {openThread === d.id && (
            <div className="mt-4 border-t border-gray-700 pt-4 space-y-3">
              {(replies[d.id] || []).map((r) => (
                <div key={r.id} className="bg-gray-800 rounded-lg p-3">
                  <p className="text-gray-300 text-sm">{r.body}</p>
                  <p className="text-gray-500 text-xs mt-1">{r.author_name}</p>
                </div>
              ))}
              <div className="flex gap-2">
                <input className={inputClass} placeholder="Write a reply..." value={replyText} onChange={(e) => setReplyText(e.target.value)} />
                <button onClick={() => postReply(d.id)} className={buttonClass}>Reply</button>
              </div>
            </div>
          )}
        </div>
      ))}
      {showForm && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg max-w-2xl w-full border border-yellow-400 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-bold text-yellow-400">Ask a Question</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white text-xl leading-none">&times;</button>
            </div>
            <div className="p-6">
              <form onSubmit={submit} className="space-y-4">
                <input required className={inputClass} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                <textarea required className={`${inputClass} min-h-[100px]`} placeholder="Your question" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setShowForm(false)} className={secondaryButtonClass}>Cancel</button>
                  <button type="submit" className={buttonClass}>Post</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentCoursePage;
